import type { ConsentStatus } from "../../../shared/api/parent";
import type { ConsentHeadline, ConsentSort } from "./consentRows";

/**
 * S3's copy.
 *
 * ⚠️ A separate file from the teacher tier's `copy.ts` rather than an addition
 * to it: `features/dashboards/school` importing from
 * `features/dashboards/teacher` is a sideways dependency, which
 * `folderstructure.md` forbids. The duplication is four strings and the
 * alternative is a shared module for one screen.
 */

export type Lang = "en";

export const SCHOOL_COPY = {
  en: {
    consentTitle: "Parental consent",
    consentSubtitle: "Who has agreed to us keeping their child’s progress",

    granted: "Consent given",
    pending: "Not yet given",
    revoked: "Withdrawn",
    expired: "Run out",
    children: "Children",

    /**
     * 🔴 THE SENTENCE THIS WHOLE SCREEN TURNS ON.
     *
     * On the day this ships, every child in every school is pending: OD-50 kept
     * the consent row unwritten until now. A school reading "18 pending" with
     * no explanation concludes we lost their records. It has to say the true
     * thing — nothing has been captured yet, and the parent OTP flow is how it
     * gets captured.
     */
    headlineNoneCaptured:
      "No consent has been captured yet. Parents give it themselves after signing in with their phone number — this is not missing data.",
    headlineSomeOutstanding:
      "Some parents have not given consent yet. Until they do, nothing is kept for those children.",
    headlineAllGranted: "Every parent in this class has given consent.",
    headlineNobodyEnrolled: "No children in this class yet.",

    /** ⚠️ Explains the consequence, so "pending" is not read as "fine for now". */
    pendingConsequence:
      "A child with no consent can play everything. Nothing they do is kept, so they will not appear in progress reports.",

    sortRoll: "By roll number",
    sortStatus: "Outstanding first",
    sortLabel: "Order",

    roll: "Roll",
    student: "Student",
    status: "Consent",
    givenOn: "Given on",
    keptUntil: "Kept until",
    withdrawnOn: "Withdrawn on",
    dash: "—",

    loadFailed: "Couldn’t load this.",
    signInMissing: "Admin sign-in isn’t built yet, so this can’t load.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",
  },
} as const;

/**
 * 🔴 S3 STILL 401s, AND SAYS SO BY NAME.
 *
 * Parent login exists; **staff login does not** (OD-51). So unlike C1–C5, this
 * screen cannot load today — and "couldn't load this" would send whoever is
 * testing the pilot build hunting a network fault that does not exist. This is
 * the same discipline `teacher/copy.ts` applies, for the same missing route.
 */
export function describeError(err: Error | null, lang: Lang): string {
  const t = SCHOOL_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return t.signInMissing;
  return t.loadFailed;
}

export function statusLabel(status: ConsentStatus, lang: Lang): string {
  const t = SCHOOL_COPY[lang];
  switch (status) {
    case "GRANTED":
      return t.granted;
    case "REVOKED":
      return t.revoked;
    case "EXPIRED":
      return t.expired;
    default:
      return t.pending;
  }
}

/**
 * ⚠️ PENDING IS NEUTRAL. See `consentRows.ts` — a wall of red on the day this
 * ships would read as a data-loss incident, and the school's first call would
 * be to us rather than to the parents.
 */
export function statusTone(
  status: ConsentStatus,
): "success" | "warning" | "neutral" {
  switch (status) {
    case "GRANTED":
      return "success";
    case "EXPIRED":
    case "REVOKED":
      return "warning";
    default:
      return "neutral";
  }
}

export function headlineText(headline: ConsentHeadline, lang: Lang): string {
  const t = SCHOOL_COPY[lang];
  switch (headline) {
    case "noneCaptured":
      return t.headlineNoneCaptured;
    case "someOutstanding":
      return t.headlineSomeOutstanding;
    case "allGranted":
      return t.headlineAllGranted;
    default:
      return t.headlineNobodyEnrolled;
  }
}

export const SORT_LABEL: Record<ConsentSort, Record<Lang, string>> = {
  roll: { en: SCHOOL_COPY.en.sortRoll },
  status: { en: SCHOOL_COPY.en.sortStatus },
};
